import { EMPTY_ARRAY } from 'constants/defaults';
import { HOUR_EDITOR_MAX_SUGGESTIONS } from 'constants/hour';
import { flatten, isEqual, uniq } from 'lodash-es';
import { getAccountCurrency, getAllProjectsLoggingEnabled } from 'redux/selectors/accounts';
import { getClients } from 'redux/selectors/clients';
import {
  getHourDraftEntryIds,
  getHourDraftForecastId,
  getHourDraftProjectId,
  getHourDraftUserId,
} from 'redux/selectors/hours.draft.base';
import {
  getAllLoadedProjects,
  getAllProjectsHaveBeenFetched,
  getPinnedProjectsForProjectDropdown,
  getProjectPermissionsMap,
  getProjectVariantMap,
  getProjectXhrStatus,
  getRecentlyUsedPinnedProjectIdsSet,
} from 'redux/selectors/projects';
import { getEntryIdPredictions } from 'redux/selectors/timeline';
import { getUser, getUserId, getUserPermissions } from 'redux/selectors/user';
import { createSelector, lruMemoize } from 'reselect';
import { getForecastsByState } from 'routes/forecast/redux/modules/forecast.selectors';
import { filterForecastListForUser } from 'routes/forecast/utils/forecast';
import type { HourEditorProjectSectionProps } from 'types/hour';
import type { EnrichedProject } from 'types/project';
import type { SuggestedEntryProject } from 'types/timeline';
import { getActiveProjects, getAvailableProjectsForUser } from 'utils/project';
import { notNull } from 'utils/typesafe';
import { getCalendarUserId } from './calendar.filter';
import { getPinnedProjectIdsSet } from './pins';
import {
  enrichProjectVariant,
  enrichProjectVariantOrNull,
  projectSelectorFilterProjectIds,
} from './utils/projects';

/**
 * Returns project ids for suggestions and "recent projects" optgroup
 * for the user we're acting as on an HourDraft
 */
export const getRecentProjectsIdsForHourDraft = createSelector(
  [getProjectPermissionsMap, getProjectVariantMap, getHourDraftUserId, getUserId],
  (permissionMap, variantMap, hourDraftUserId, authenticatedUserId): Array<number> => {
    const permissions = permissionMap[hourDraftUserId ?? authenticatedUserId] ?? [];
    return projectSelectorFilterProjectIds(permissions, variantMap);
  }
);

export const getRecentProjectsForHourDraft = createSelector(
  [getRecentProjectsIdsForHourDraft, getProjectVariantMap, getRecentlyUsedPinnedProjectIdsSet],
  (projectIds, projectById, recentlyUsedPinnedProjectIdsSet): Array<EnrichedProject> => {
    return projectIds
      .filter(projectId => !recentlyUsedPinnedProjectIdsSet.has(projectId))
      .map(projectId => {
        return enrichProjectVariantOrNull(projectById[projectId], recentlyUsedPinnedProjectIdsSet);
      })
      .filter(notNull);
  }
);

export const getSuggestedProjectsForHourDraft = createSelector(
  [
    getRecentProjectsIdsForHourDraft,
    getProjectVariantMap,
    getHourDraftEntryIds,
    getEntryIdPredictions,
    getPinnedProjectIdsSet,
  ],
  (
    recentProjectIds,
    projectVariantMap,
    selectedEntryIds,
    entryIdPredictions,
    pinnedProjectIdsSet
  ): Array<SuggestedEntryProject> => {
    // Order the entry ids by the predicted project's accuracy
    const predictedProjectIds = selectedEntryIds
      .filter(entryId => entryIdPredictions[entryId] && entryIdPredictions[entryId].accuracy > 0)
      .sort((a, b) => {
        return entryIdPredictions[b].accuracy - entryIdPredictions[a].accuracy;
      })
      .map(entryId => entryIdPredictions[entryId].project_id);

    // Get the 3 highest predictions (and include recent projects if needed)
    // Flatten since EntryIdPredictionsMap[k].project_id is typed as an array
    const suggestedProjects = uniq(flatten([...predictedProjectIds, ...recentProjectIds]))
      .map(projectId => {
        const variant = projectVariantMap[projectId];
        if (!variant) {
          return null;
        }
        return {
          ...enrichProjectVariant(variant, pinnedProjectIdsSet),
          accuracy: 0,
        };
      })
      .filter(notNull)
      .slice(0, HOUR_EDITOR_MAX_SUGGESTIONS);

    return suggestedProjects.length ? suggestedProjects : EMPTY_ARRAY;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getPermissionsForDraftOwner = createSelector(
  [getHourDraftUserId, getProjectPermissionsMap],
  (userId, permissions) => permissions[userId!] ?? EMPTY_ARRAY
);

// NB: This is more or less a direct copy of selectors/projects::projectsForUser
// This method could use getProjectEntities and a similar structure for permissions
// Maybe re-write and loop over permissions instead of projects?
export const getProjectsForDraftUserCore = createSelector(
  [
    getAllLoadedProjects,
    getClients,
    getPermissionsForDraftOwner,
    getHourDraftProjectId,
    getAllProjectsLoggingEnabled,
  ],
  (projects, clients, permissions, hourDraftProjectId, allProjectsLogging) => {
    const archivedClientIds = clients.filter(client => !client.active).map(client => client.id);

    // When ALL_PROJECTS_LOGGING is on, the hour-editor picker should list
    // every active workspace project regardless of which projects the user
    // is currently a member of. Use the dedicated O(n) active-projects
    // helper instead of synthesizing a permission list (which would force
    // an O(n*m) includes() check per project). Backend auto-joins on save.
    if (allProjectsLogging) {
      const activeProjects = getActiveProjects(projects, archivedClientIds);
      if (hourDraftProjectId && !activeProjects.some(p => p.base.id === hourDraftProjectId)) {
        const selected = projects.find(p => p.base.id === hourDraftProjectId);
        if (selected) {
          return [...activeProjects, selected];
        }
      }
      return activeProjects;
    }

    return getAvailableProjectsForUser(
      projects,
      false,
      archivedClientIds,
      permissions,
      hourDraftProjectId ? [hourDraftProjectId] : []
    );
  }
);

/**
 * Wraps `getProjectsForDraftUserCore` with deep equality memoization, to avoid producing a new
 * `projects` instance when `hourDraftProjectId` changes but the same projects are included.
 */
export const getProjectsForDraftUser = createSelector(
  [getProjectsForDraftUserCore],
  projects => projects,
  { memoize: lruMemoize, memoizeOptions: { equalityCheck: isEqual } }
);

/**
 * Get forecasts assigned to the owner of the hour draft
 */
export const getForecastsForDraftUser = createSelector(
  [getForecastsByState, getCalendarUserId, getHourDraftForecastId],
  ({ active }, actingUserId, forecastId) => {
    if (!actingUserId) {
      return [];
    }
    return active.filter(forecast => {
      const forecastIsLinkedToDraft = forecast.id === forecastId;
      return (
        filterForecastListForUser(forecast, {
          userId: actingUserId,
          excludeCompletedForecasts: !forecastIsLinkedToDraft,
        }) || forecastIsLinkedToDraft
      );
    });
  }
);

export const getHourEditorProjectSectionProps = createSelector(
  [
    getAccountCurrency,
    getProjectsForDraftUser,
    getRecentProjectsForHourDraft,
    getPinnedProjectsForProjectDropdown,
    getSuggestedProjectsForHourDraft,
    getUser,
    getUserPermissions,
    getHourDraftUserId,
    getHourDraftProjectId,
    getClients,
    getProjectXhrStatus,
    getAllProjectsHaveBeenFetched,
    getForecastsForDraftUser,
  ],
  (
    currency,
    projects,
    recentProjects,
    pinnedProjects,
    suggestedProjects,
    user,
    userPermissions,
    actingUserId,
    selectedProjectId,
    clients,
    { isLoading },
    allProjectsLoaded,
    forecasts
  ): HourEditorProjectSectionProps => {
    const effectiveUserId = actingUserId ?? user.id;
    const isActingAsOtherUser = actingUserId !== user.id;
    const hasForecasts = forecasts.length > 0;

    // Determine how many forecasts or projects should be suggested:
    const showForecastSuggestions = hasForecasts && !selectedProjectId;
    const numForecastSuggestions = Math.min(forecasts.length, HOUR_EDITOR_MAX_SUGGESTIONS);
    const hasRoomForProjectSuggestions = numForecastSuggestions < HOUR_EDITOR_MAX_SUGGESTIONS;
    const showProjectSuggestions = hasRoomForProjectSuggestions && !selectedProjectId;
    const numProjectSuggestions = Math.min(
      suggestedProjects.length,
      HOUR_EDITOR_MAX_SUGGESTIONS - numForecastSuggestions
    );

    return {
      projects,
      recentProjects,
      pinnedProjects,
      suggestions: suggestedProjects,
      forecasts,
      clients,
      currency,
      userId: effectiveUserId,
      ui: {
        isActingAsOtherUser,
        showForecastSuggestions,
        numForecastSuggestions,
        showProjectSuggestions,
        numProjectSuggestions,
        selectedProjectId,
        allowCreateProject: userPermissions.canCreate.projects,
        isLoading,
        placeholder: showForecastSuggestions
          ? 'Select Task or Project'
          : showProjectSuggestions
            ? 'Select Project'
            : hasForecasts
              ? 'Search Client, Project or Task'
              : 'Search Client or Project',
        allProjectsLoaded,
      },
    };
  }
);
